/* eslint-disable react/no-unstable-nested-components */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "components/ui/classNames";
import { ScrollArea } from "components/ui/ScrollArea";
import TablePagination from "components/ui/Table/TablePagination";
import differenceInYears from "date-fns/differenceInYears";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface AmericanDividendsPaidProps {
  dividendsPaid: IDividendsPaidDetails[];
}

export default function AmericanDividendsPaid({ dividendsPaid }: AmericanDividendsPaidProps) {
  const intl = useIntl();
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();

  const columns = [
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className={classNames("w-40 px-2 py-1.5 text-sm font-semibold text-slate-600")}>
          <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames("w-40 select-none whitespace-nowrap px-2 py-1.5 text-sm font-semibold text-slate-600")}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
      meta: {
        header: {
          className: classNames("sticky left-0 w-40"),
        },
      },
    }),
    columnHelper.accessor("payDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amountPaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Valor ($)" description="Valor dos dividendos pagos em dólar" id="Iwb0Ka" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{trimEnd(trimEnd(numeral(info.getValue()).format("0.000").replace(".", ","), "0"), ",")}</span>
        </div>
      ),
    }),
    columnHelper.accessor("distributionFrequency", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Frequência" id="0w0ol9" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap px-4 py-1.5 text-sm font-normal text-slate-600 hover:bg-teal-300/25 dark:text-slate-400 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{getLabelForDividendFrequency(info.getValue())}</span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: dividendsPaid,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 16,
      },
    },
  });

  return (
    <>
      <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
        <div className="pb-4 lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 bg-slate-50 p-0 text-center",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>

      <TablePagination<IDividendsPaidDetails> table={table} />
    </>
  );
}

export function getExcludingDate(intl: IntlShape, recordDate: Date, excludingDate: Date) {
  const difference = differenceInYears(excludingDate, recordDate);

  if (difference >= 2) {
    return "-";
  }

  return intl.formatDate(excludingDate, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function getLabelForDividendFrequency(value?: number | null) {
  switch (value) {
    case 1:
      return (
        <FormattedMessage
          defaultMessage="Anual"
          description="Periodicidade para distribuição dos dividendos"
          id="v0bpXX"
        />
      );
    case 2:
      return (
        <FormattedMessage
          defaultMessage="Semestral"
          description="Periodicidade para distribuição dos dividendos"
          id="521Yp1"
        />
      );
    case 4:
      return (
        <FormattedMessage
          defaultMessage="Trimestral"
          description="Periodicidade para distribuição dos dividendos"
          id="pKAHhM"
        />
      );
    case 12:
      return (
        <FormattedMessage
          defaultMessage="Mensal"
          description="Periodicidade para distribuição dos dividendos"
          id="Up/Fj3"
        />
      );

    default:
      return "-";
  }
}
